import { Config } from '../../../video-data/config';
import { TextAlignment } from '../../../enums';
import { isBreakingSpace } from '../textMetricsHelpers';
import type { CanvasWordMetrics } from './CanvasWordMetrics';
import { CanvasTextMetricList } from './CanvasTextMetricList';
import { CanvasHighlightSpanMetrics } from './CanvasHighlightSpanMetrics';
import type { CanvasWordElementMetrics } from './CanvasWordElementMetrics';


/**
 * CanvasTextLineMetrics is a class that represents the measurement of a line of text.
 */
export class CanvasTextLineMetrics extends CanvasTextMetricList<CanvasWordMetrics> {
    #removedLeadingSpace = false;
    #removedTrailingSpace = false;
    #visibleWords: CanvasWordMetrics[] = null;
    #highlightSpans: CanvasHighlightSpanMetrics[] = null;

    add(wordMetrics: CanvasWordMetrics) {
        this.children.push(wordMetrics);
        this.clearCachedValues();
    }

    get words(): CanvasWordMetrics[] {
        return this.children;
    }

    /**
     * Returns the highlight spans for this line. A highlight span is a contiguous sequence of highlighted word elements.
     * It will span multiple words if they are all highlighted, including spaces in between. It will not include
     * leading or trailing spaces.
     */
    get highlightSpans(): CanvasHighlightSpanMetrics[] {
        if (!this.#highlightSpans) {
            this.#highlightSpans = this.#calculateHighlightSpans();
        }
        return this.#highlightSpans;
    }

    #calculateHighlightSpans(): CanvasHighlightSpanMetrics[] {
        // infer the highlight spans from the current word metrics
        const highlightSpans = [];
        let currentWordElements: CanvasWordElementMetrics[] = [];
        let spaceWordElements: CanvasWordElementMetrics[] = [];
        this.words.forEach(word => {
            word.elements.forEach(element => {
                // filter out leading & trailing spaces (only spaces within a span are kept)
                if (isBreakingSpace(element.text)) {
                    if (currentWordElements.length === 0) {
                        return;
                    }
                    spaceWordElements.push(element);
                    return;
                }
                if (element.highlighted) {
                    // This is either the start of a new highlight span or a continuation of the current one
                    if (spaceWordElements.length > 0) {
                        // add any previous spaces first
                        currentWordElements.push(...spaceWordElements);
                        spaceWordElements = [];
                    }
                    currentWordElements.push(element);
                }
                else if (currentWordElements.length > 0) {
                    // we've reached a non-highlighted word, so if there are highlighted words that means we have completed a span
                    highlightSpans.push(new CanvasHighlightSpanMetrics(currentWordElements));
                    currentWordElements = [];
                    spaceWordElements = [];
                }
            });
        });
        if (currentWordElements.length > 0) {
            // create a span for the last highlight, which went right up until the end of the line
            highlightSpans.push(new CanvasHighlightSpanMetrics(currentWordElements));
        }
        return highlightSpans;
    }

    /**
     * @returns word metrics that exclude breaking spaces.
     */
    get visibleWords(): CanvasWordMetrics[] {
        if (!this.#visibleWords) {
            this.#visibleWords = this.children.filter(child => !isBreakingSpace(child.text));
        }
        return this.#visibleWords;
    }

    setAlignmentAndReadingDirection(maxLineWidth: number, alignment: TextAlignment) {
        let lineOffsetX = 0;
        const lineWidth = this.width;
        this.words.forEach(word => {
            switch (alignment) {
                case TextAlignment.LEFT: lineOffsetX = 0; break;
                case TextAlignment.CENTER: lineOffsetX = (maxLineWidth - lineWidth) / 2; break;
                case TextAlignment.RIGHT: lineOffsetX = maxLineWidth - lineWidth; break;
            }
            // If reading RTL then the offset is from the end of the line
            if (this.style.readingDirection === Config.READING_DIRECTION.RTL) {
                lineOffsetX += lineWidth;
            }

            word.setReadingDirection(lineOffsetX);
        });
    }

    get removedLeadingSpace(): boolean {
        return this.#removedLeadingSpace;
    }

    get removedTrailingSpace(): boolean {
        return this.#removedTrailingSpace;
    }

    removeLeadingTrailingSpaces(alignment: TextAlignment) {
        if (this.words.length === 0) {
            return;
        }
        let leadingWord: CanvasWordMetrics;
        let trailingWord: CanvasWordMetrics;
        switch (alignment) {
            case TextAlignment.LEFT:
                leadingWord = this.words[0];
                break;
            case TextAlignment.CENTER:
                leadingWord = this.words[0];
                trailingWord = this.words[this.words.length - 1];
                break;
            case TextAlignment.RIGHT:
                trailingWord = this.words[this.words.length - 1];
                break;
        }
        // remove a leading space if it hasn't already been removed
        if (!this.#removedLeadingSpace && !!leadingWord && isBreakingSpace(leadingWord.text)) {
            leadingWord.resetWordElementMetrics();
            this.#removedLeadingSpace = true;
            this.clearCachedValues();
        }
        // remove a trailing space if it hasn't already been removed
        if (!this.#removedTrailingSpace && !!trailingWord && isBreakingSpace(trailingWord.text)) {
            trailingWord.resetWordElementMetrics();
            this.#removedTrailingSpace = true;
            this.clearCachedValues();
        }
    }

    removeOrphans(): CanvasWordMetrics[] {
        const wordMetrices = [];

        const wordMetrics = this.children.pop();
        wordMetrices.push(wordMetrics);
        if (isBreakingSpace(wordMetrics.text)) {
            const wordMetrics = this.children.pop();
            wordMetrices.unshift(wordMetrics);
        }

        this.clearCachedValues();
        return wordMetrices;
    }

    removeEndOfLinePunctuation(): boolean {
        const lastWord = this.words.at(-1);
        if (!lastWord.endsWithEndOfLinePunctuation()) {
            return false;
        }
        lastWord.removeLastCharacter();
        this.clearCachedValues();
        return true;
    }

    getIndexOfInLinePunctuation(): number {
        return this.words.findLastIndex(word => word.endsWithInLinePunctuation());
    }

    hasInLinePunctuations(): boolean {
        return this.getIndexOfInLinePunctuation() !== -1;
    }

    removeWordsAfterLastInLinePunctuation(): CanvasWordMetrics[] {
        const punctuationIndex = this.getIndexOfInLinePunctuation();
        if (punctuationIndex === -1) {
            return [];
        }
        const wordsToMove = this.words.slice(punctuationIndex + 1);
        this.removeChildrenFromEnd(wordsToMove.length);
        return wordsToMove;
    }

    override clearCachedValues(): void {
        super.clearCachedValues();
        this.#visibleWords = null;
        this.#highlightSpans = null;
    }

    override _getSourceWordReferenceIndices(): number[] {
        return this.words.map(word => word.sourceWordReferenceIndex).filter(index => index !== -1);
    }
}

